import React, { useState, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RequestCallButton } from "@/components/RequestCallButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Calculator, Loader2, Trash2, TrendingUp, ChevronDown, ChevronUp, Search } from "lucide-react";

const money = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

const confidenceColor = (c) => {
  if (c === "High") return "bg-emerald-100 text-emerald-700";
  if (c === "Low") return "bg-amber-100 text-amber-700";
  return "bg-sky-100 text-sky-700";
};

function EstimateResult({ est }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="bg-[#547d91]/5 p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="font-medium text-slate-800">{est.address}</p>
          <Badge className={`${confidenceColor(est.confidence)} hover:${confidenceColor(est.confidence)}`}>
            {est.confidence} confidence
          </Badge>
        </div>
        <div className="mt-3 flex items-end gap-3 flex-wrap">
          <div>
            <p className="text-xs text-slate-500">Estimated value</p>
            <p className="text-3xl font-bold text-slate-900">{money(est.point_estimate)}</p>
          </div>
          <p className="text-slate-500 mb-1">
            Range {money(est.estimated_low)} – {money(est.estimated_high)}
          </p>
        </div>
        {est.summary && <p className="text-sm text-slate-600 mt-2">{est.summary}</p>}
      </div>

      {est.portal_estimate && (est.portal_estimate.low || est.portal_estimate.mid) && (
        <div className="px-4 pt-3">
          <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
            <p className="text-xs font-semibold text-emerald-800 mb-0.5">
              property.com.au estimate {est.portal_estimate.mid ? `· ${est.portal_estimate.mid}` : ""}
            </p>
            <p className="text-sm text-emerald-700">
              {est.portal_estimate.low} – {est.portal_estimate.high}
              {est.portal_estimate.source_url && (
                <>
                  {" · "}
                  <a href={est.portal_estimate.source_url} target="_blank" rel="noopener noreferrer" className="underline">
                    source
                  </a>
                </>
              )}
            </p>
          </div>
        </div>
      )}
      {est.rationale && (
        <div className="p-4">
          <button
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-1 text-sm font-medium text-[#547d91]"
          >
            Pricing rationale {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {open && (
            <div className="advisor-md text-slate-700 mt-2">
              <ReactMarkdown>{est.rationale}</ReactMarkdown>
            </div>
          )}
        </div>
      )}

      {Array.isArray(est.comparable_sales) && est.comparable_sales.length > 0 && (
        <div className="px-4 pb-3">
          <p className="text-sm font-semibold text-slate-700 mb-2">Comparable sales</p>
          <div className="space-y-2">
            {est.comparable_sales.map((c, i) => (
              <div key={i} className="text-sm bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-medium text-slate-800">{c.address}</span>
                  <span className="text-[#547d91] font-medium">
                    {c.sale_price}{c.sale_date ? ` · ${c.sale_date}` : ""}
                  </span>
                </div>
                {c.note && <p className="text-xs text-slate-500 mt-0.5">{c.note}</p>}
                {c.source_url && (
                  <a href={c.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#547d91] hover:underline">
                    View source
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(est.citations) && est.citations.length > 0 && (
        <div className="px-4 pb-4">
          <p className="text-sm font-semibold text-slate-700 mb-1">Sources</p>
          <ul className="space-y-0.5">
            {est.citations.slice(0, 12).map((c, i) => (
              <li key={i} className="truncate">
                <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#547d91] hover:underline">
                  {c.title || c.url}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function PriceEstimateTool() {
  const [form, setForm] = useState({
    address: "",
    property_type: "House",
    bedrooms: "",
    bathrooms: "",
    car_spaces: "",
    land_size_sqm: "",
    condition: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [looking, setLooking] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get("/research/price-estimates");
      setHistory(res.data.estimates || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleLookup = async () => {
    if (!form.address.trim()) {
      toast.error("Please enter a property address first.");
      return;
    }
    setLooking(true);
    try {
      const res = await api.post("/research/property-lookup", { address: form.address.trim() });
      const d = res.data || {};
      if (!d.found && d.bedrooms == null && d.bathrooms == null && d.land_size_sqm == null) {
        toast.info("Couldn't find details for this address. Please enter them manually.");
        return;
      }
      setForm((f) => ({
        ...f,
        property_type: d.property_type || f.property_type,
        bedrooms: d.bedrooms != null ? String(d.bedrooms) : f.bedrooms,
        bathrooms: d.bathrooms != null ? String(d.bathrooms) : f.bathrooms,
        car_spaces: d.car_spaces != null ? String(d.car_spaces) : f.car_spaces,
        land_size_sqm: d.land_size_sqm != null ? String(d.land_size_sqm) : f.land_size_sqm,
        condition: d.condition || f.condition,
      }));
      toast.success("Property details filled in. Please review and correct if needed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not look up property details.");
    } finally {
      setLooking(false);
    }
  };

  const handleEstimate = async () => {
    if (!form.address.trim()) {
      toast.error("Please enter a property address.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        address: form.address.trim(),
        property_type: form.property_type || null,
        bedrooms: form.bedrooms !== "" ? parseInt(form.bedrooms, 10) : null,
        bathrooms: form.bathrooms !== "" ? parseInt(form.bathrooms, 10) : null,
        car_spaces: form.car_spaces !== "" ? parseInt(form.car_spaces, 10) : null,
        land_size_sqm: form.land_size_sqm !== "" ? parseFloat(form.land_size_sqm) : null,
        condition: form.condition || null,
        notes: form.notes || null,
      };
      const res = await api.post("/research/price-estimate", payload);
      setResult(res.data);
      setHistory((prev) => [res.data, ...prev]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate estimate. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (estimateId) => {
    try {
      await api.delete(`/research/price-estimates/${estimateId}`);
      setHistory((prev) => prev.filter((e) => e.estimate_id !== estimateId));
      if (result?.estimate_id === estimateId) setResult(null);
      toast.success("Estimate removed.");
    } catch (err) {
      toast.error("Could not delete estimate.");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-8 h-8 rounded-lg bg-[#547d91]/10 flex items-center justify-center">
                <Calculator className="w-4 h-4 text-[#547d91]" />
              </div>
              AI Price Estimate
            </CardTitle>
            <p className="text-sm text-slate-500 mt-2">
              Indicative value using the direct comparison method, powered by live web search of recent
              comparable sales (with sources). Estimate only — verify independently before making decisions.
            </p>
          </div>
          <RequestCallButton
            tool="AI Price Estimate"
            defaultAddress={form.address}
            resultSummary={result ? `AI Price Estimate: ${money(result.point_estimate)} (range ${money(result.estimated_low)} – ${money(result.estimated_high)})${result.confidence ? `, ${result.confidence} confidence` : ""}.${result.summary ? `\n${result.summary}` : ""}` : ""}
            className="shrink-0"
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="pe-address">Property Address *</Label>
          <div className="flex gap-2">
            <div className="flex-1">
              <AddressAutocomplete
                id="pe-address"
                data-testid="pe-address"
                placeholder="e.g. 12 Latrobe Terrace, Paddington QLD"
                value={form.address}
                onChange={(v) => update("address", v)}
                onAddressSelect={(a) => update("address", a.formattedAddress || a.streetAddress)}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleLookup}
              disabled={looking}
              data-testid="pe-lookup-btn"
              className="shrink-0"
            >
              {looking ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Looking up...</>
              ) : (
                <><Search className="w-4 h-4 mr-2" /> Look up details</>
              )}
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Enter the address and click "Look up details" to auto-fill features from the web. You can edit any field before estimating.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label htmlFor="pe-type" className="text-xs">Property type</Label>
            <select
              id="pe-type"
              value={form.property_type}
              onChange={(e) => update("property_type", e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white"
            >
              {["House", "Unit", "Townhouse", "Apartment", "Land", "Other"].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="pe-beds" className="text-xs">Bedrooms</Label>
            <Input id="pe-beds" data-testid="pe-beds" type="number" min="0" value={form.bedrooms} onChange={(e) => update("bedrooms", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pe-baths" className="text-xs">Bathrooms</Label>
            <Input id="pe-baths" type="number" min="0" value={form.bathrooms} onChange={(e) => update("bathrooms", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pe-car" className="text-xs">Car spaces</Label>
            <Input id="pe-car" type="number" min="0" value={form.car_spaces} onChange={(e) => update("car_spaces", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pe-land" className="text-xs">Land size (sqm)</Label>
            <Input id="pe-land" type="number" min="0" value={form.land_size_sqm} onChange={(e) => update("land_size_sqm", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pe-condition" className="text-xs">Condition</Label>
            <Input id="pe-condition" placeholder="e.g. Renovated" value={form.condition} onChange={(e) => update("condition", e.target.value)} />
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="pe-notes" className="text-xs">Notes / key features (optional)</Label>
          <Input id="pe-notes" placeholder="e.g. city views, pool, corner block, needs work" value={form.notes} onChange={(e) => update("notes", e.target.value)} />
        </div>

        <Button onClick={handleEstimate} disabled={loading} data-testid="pe-estimate-btn" className="w-full sm:w-auto">
          {loading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Estimating...</>
          ) : (
            <><TrendingUp className="w-4 h-4 mr-2" /> Estimate value</>
          )}
        </Button>

        {result && (
          <div className="pt-2" data-testid="pe-result">
            <EstimateResult est={result} />
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className="pt-2">
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Estimate History</h4>
            <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
              {history.map((e) => (
                <div key={e.estimate_id} className="flex items-center justify-between gap-3 p-3" data-testid="pe-history-row">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 truncate">{e.address}</p>
                    <p className="text-xs text-slate-500">
                      {money(e.point_estimate)} ({money(e.estimated_low)}–{money(e.estimated_high)}) · {formatDate(e.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => setResult(e)}>View</Button>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDelete(e.estimate_id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
